import React, { useEffect, useMemo, useState } from "react"
import { observer } from "mobx-react-lite"
import { FormikProvider, useFormik } from "formik"
import * as yup from "yup"

import { useController, useStore } from "@store"
import Loader from "@components/ui/Loader/BarLoader"
import Text from "@components/ui/Typography/Text"
import Button from "@components/ui/Button/Button"
import FormTextInput from "@components/prototypes/form-elements/FormTextInput"
import Select from "@components/ui/MultiSelect/Select"
import TextInput from "@components/ui/TextInput/TextInput"
import NotFound from "@components/ui/NotFound/NotFound"
import { ConnectorPathMatchType } from "@framework/types/content-manager"

import styles from "./FolderMappingsTab.module.sass"

const matchTypeOptions = [
  { name: "contains", value: "Contains" },
  { name: "starts_with", value: "Starts with" },
  { name: "regex", value: "Regex" },
]

const emptyBulkRow = () => ({
  id: `${Date.now()}-${Math.random()}`,
  urlPattern: "",
  matchType: "contains" as ConnectorPathMatchType,
  v2CompanyId: "",
  priority: 0,
})

const validationSchema = yup.object({
  urlPattern: yup.string().required("URL pattern is required"),
  matchType: yup
    .mixed<ConnectorPathMatchType>()
    .oneOf(["contains", "starts_with", "regex"])
    .required("Match type is required"),
  v2CompanyId: yup.string().required("Manufacturer is required"),
  priority: yup.number().min(0).optional(),
})

type FolderMappingsTabProps = {
  connectorId: string
}

const FolderMappingsTab: React.FC<FolderMappingsTabProps> = observer(
  ({ connectorId }) => {
    const { contentManagerController } = useController()
    const { contentManagerStore } = useStore()
    const {
      connectorPathMappings,
      loadingConnectorPathMappings,
      savingConnectorPathMapping,
      savingBulkConnectorPathMappings,
      bulkMappingResult,
      companies,
      loadingCompanies,
      error,
    } = contentManagerStore

    const [bulkRows, setBulkRows] = useState([emptyBulkRow(), emptyBulkRow(), emptyBulkRow()])

    const companyOptions = useMemo(
      () =>
        companies?.map((company) => ({
          name: company.id,
          value: company.name,
        })) || [],
      [companies]
    )

    const formik = useFormik({
      initialValues: {
        urlPattern: "",
        matchType: "contains" as ConnectorPathMatchType,
        v2CompanyId: "",
        priority: 0,
      },
      validationSchema,
      onSubmit: async (values, { resetForm }) => {
        const success = await contentManagerController.addConnectorPathMapping(
          connectorId,
          {
            urlPattern: values.urlPattern.trim(),
            matchType: values.matchType,
            v2CompanyId: values.v2CompanyId,
            priority: Number(values.priority) || 0,
          }
        )

        if (success) {
          resetForm()
        }
      },
    })

    useEffect(() => {
      contentManagerController.loadConnectorPathMappings(connectorId)
      contentManagerController.getCompanies()
    }, [connectorId, contentManagerController])

    const handleBulkSubmit = async () => {
      const mappings = bulkRows
        .map((row) => ({
          urlPattern: row.urlPattern.trim(),
          matchType: row.matchType,
          v2CompanyId: row.v2CompanyId,
          priority: Number(row.priority) || 0,
        }))
        .filter((row) => row.urlPattern && row.v2CompanyId)

      if (!mappings.length) {
        contentManagerStore.error = "Add at least one complete bulk mapping row."
        return
      }

      const success = await contentManagerController.bulkAddConnectorPathMappings(
        connectorId,
        mappings
      )

      if (success) {
        setBulkRows([emptyBulkRow(), emptyBulkRow(), emptyBulkRow()])
      }
    }

    if (loadingConnectorPathMappings || loadingCompanies) {
      return <Loader size="large" fluid />
    }

    return (
      <div className={styles.root}>
        <Text variant="body2" className={styles.description}>
          Map SharePoint URL fragments to manufacturers. Lower priority numbers are
          evaluated first. Use URL-encoded paths (for example %20 for spaces).
        </Text>

        <FormikProvider value={formik}>
          <form className={styles.form} onSubmit={formik.handleSubmit}>
            <Text variant="h4" className={styles.sectionTitle}>
              Add single mapping
            </Text>
            <div className={styles.field}>
              <Text variant="body2">URL pattern</Text>
              <FormTextInput
                name="urlPattern"
                placeholder="/TA%20Instruments/01%20Brochure/"
              />
            </div>

            <div className={styles.field}>
              <Text variant="body2">Match type</Text>
              <Select
                options={matchTypeOptions}
                value={formik.values.matchType}
                onChange={(value) =>
                  formik.setFieldValue("matchType", value || "contains")
                }
              />
            </div>

            <div className={styles.field}>
              <Text variant="body2">Manufacturer</Text>
              <Select
                placeholder="Select manufacturer"
                options={companyOptions}
                value={formik.values.v2CompanyId || null}
                onChange={(value) => {
                  formik.setFieldValue("v2CompanyId", value || "")
                  formik.setFieldTouched("v2CompanyId", true)
                }}
                withError={
                  !!(formik.touched.v2CompanyId && formik.errors.v2CompanyId)
                }
              />
            </div>

            <div className={styles.field}>
              <Text variant="body2">Priority</Text>
              <FormTextInput name="priority" type="number" />
            </div>

            {error && (
              <Text variant="body2" className={styles.error}>
                {error}
              </Text>
            )}

            <Button
              type="button"
              color="primary"
              disabled={savingConnectorPathMapping}
              onClick={() => {
                formik.setTouched({
                  urlPattern: true,
                  matchType: true,
                  v2CompanyId: true,
                  priority: true,
                })
                formik.handleSubmit()
              }}
            >
              Add mapping
            </Button>
          </form>
        </FormikProvider>

        <div className={styles.bulkSection}>
          <Text variant="h4" className={styles.sectionTitle}>
            Bulk add mappings
          </Text>
          <Text variant="body2" className={styles.description}>
            Add multiple manufacturer folder mappings at once. Fill each row, then
            click bulk add.
          </Text>

          {bulkRows.map((row, index) => (
            <div key={row.id} className={styles.bulkRow}>
              <div className={styles.field}>
                <Text variant="body2">URL pattern</Text>
                <TextInput
                  value={row.urlPattern}
                  onChange={(event) => {
                    const nextRows = [...bulkRows]
                    nextRows[index] = {
                      ...nextRows[index],
                      urlPattern: event.target.value,
                    }
                    setBulkRows(nextRows)
                  }}
                  placeholder="Biolin%20Scientific"
                />
              </div>

              <div className={styles.field}>
                <Text variant="body2">Match type</Text>
                <Select
                  options={matchTypeOptions}
                  value={row.matchType}
                  onChange={(value) => {
                    const nextRows = [...bulkRows]
                    nextRows[index] = {
                      ...nextRows[index],
                      matchType: (value || "contains") as ConnectorPathMatchType,
                    }
                    setBulkRows(nextRows)
                  }}
                />
              </div>

              <div className={styles.field}>
                <Text variant="body2">Manufacturer</Text>
                <Select
                  placeholder="Select manufacturer"
                  options={companyOptions}
                  value={row.v2CompanyId || null}
                  onChange={(value) => {
                    const nextRows = [...bulkRows]
                    nextRows[index] = {
                      ...nextRows[index],
                      v2CompanyId: value || "",
                    }
                    setBulkRows(nextRows)
                  }}
                />
              </div>

              <div className={styles.field}>
                <Text variant="body2">Priority</Text>
                <TextInput
                  type="number"
                  value={row.priority}
                  onChange={(event) => {
                    const nextRows = [...bulkRows]
                    nextRows[index] = {
                      ...nextRows[index],
                      priority: Number(event.target.value) || 0,
                    }
                    setBulkRows(nextRows)
                  }}
                />
              </div>

              <div className={styles.bulkRowActions}>
                <Button
                  variant="outlined"
                  color="red"
                  onClick={() =>
                    setBulkRows(bulkRows.filter((item) => item.id !== row.id))
                  }
                >
                  Remove row
                </Button>
              </div>
            </div>
          ))}

          <div className={styles.bulkActions}>
            <Button variant="outlined" onClick={() => setBulkRows([...bulkRows, emptyBulkRow()])}>
              Add row
            </Button>
            <Button
              color="primary"
              disabled={savingBulkConnectorPathMappings}
              onClick={handleBulkSubmit}
            >
              Bulk add mappings
            </Button>
          </div>

          {bulkMappingResult && (
            <Text variant="body2" className={styles.success}>
              {bulkMappingResult}
            </Text>
          )}
        </div>

        <div className={styles.list}>
          <Text variant="h4">Current mappings</Text>
          {!connectorPathMappings.length ? (
            <NotFound>No folder mappings configured yet</NotFound>
          ) : (
            connectorPathMappings.map((mapping) => (
              <div key={mapping.id} className={styles.mappingRow}>
                <div className={styles.mappingInfo}>
                  <Text variant="body1">{mapping.urlPattern}</Text>
                  <Text variant="caption1">
                    {mapping.matchType} · priority {mapping.priority} ·{" "}
                    {mapping.companyName || mapping.v2CompanyId}
                  </Text>
                </div>
                <Button
                  variant="outlined"
                  color="red"
                  onClick={() =>
                    contentManagerController.deleteConnectorPathMapping(
                      connectorId,
                      mapping.id
                    )
                  }
                >
                  Remove
                </Button>
              </div>
            ))
          )}
        </div>
      </div>
    )
  }
)

export default FolderMappingsTab
